import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { glyph, type GlyphId } from '../../../components/glyphs';
import { brandLogo } from '../../../components/brandLogo';
import { products } from '../../../presentation/catalogue';
import { words, revealWords, settleWords } from '../../../components/text';

/** The three kinds of work named in the owner's role statement. */
const CAPABILITIES: ReadonlyArray<{ label: string; glyph: GlyphId }> = [
  { label: 'IT consulting', glyph: 'team' },
  { label: 'Specialist services', glyph: 'wrench' },
  { label: 'AI-native transformation', glyph: 'layers' },
];

/**
 * Native follows its guideline (pp. 5, 9, 10): navy carries the layout, Signal Blue is used
 * sparingly, text is left-aligned and one element leads. The monogram watermark and the thin
 * gradient rule are the guideline's own graphic elements; the logo is the unmodified master.
 *
 * 0 Navy takeover; the logo resolves with the role line
 * 1 Three capabilities on one line
 * 2 A separate Moderta brand, with its own identity.
 */
export class NativeScene extends BaseScene {
  public readonly stepCount = 3;

  protected template(): string {
    const pills = CAPABILITIES.map((c, i) => `
      <li class="mn-pill" data-index="${i}"><span class="mn-pill__icon">${glyph(c.glyph, 'glyph')}</span>${c.label}</li>`).join('');
    return `
      <div class="mn-flood" aria-hidden="true"></div>
      <img class="mn-watermark" src="/branding/native/moderta-native-monogram-white.svg" alt="" aria-hidden="true" draggable="false">
      <div class="scene-layout native">
        <div class="artboard">
          <div class="mn-lead">
            <h2 class="mn-logo">${brandLogo('native', { height: 138 })}</h2>
            <span class="mn-rule" aria-hidden="true"></span>
            <p class="mn-role">${products.native.tagline}</p>
          </div>
          <ul class="mn-pills" data-from="1" aria-label="Capabilities">
            <span class="mn-pills__line" aria-hidden="true"></span>
            ${pills}
          </ul>
          <h2 class="mn-statement" data-from="2">${words('A separate Moderta brand, with its own identity.')}</h2>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    // Brand takeover: navy sweeps in from the right, the watermark settles, then the logo resolves.
    tl.fromTo(this.$('.mn-flood'), { clipPath: 'inset(0 0 0 100%)' }, { clipPath: 'inset(0 0 0 0%)', duration: 0.9, ease: 'expo.inOut' }, 0);
    tl.fromTo(this.$('.mn-watermark'), { opacity: 0, xPercent: 12 }, { opacity: 0.09, xPercent: 0, duration: 1.6, ease: 'expo.out' }, 0.35);
    tl.fromTo(this.$('.mn-logo'), { opacity: 0, clipPath: 'inset(0 100% 0 0)', x: -12 }, { opacity: 1, clipPath: 'inset(0 0% 0 0)', x: 0, duration: 1.2, ease: 'expo.out' }, 0.7);
    tl.fromTo(this.$('.mn-rule'), { scaleX: 0 }, { scaleX: 1, duration: 0.9, ease: 'expo.inOut' }, 1.2);
    tl.fromTo(this.$('.mn-role'), { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, 1.45);
  }

  private settleLead(): void {
    gsap.set(this.$('.mn-flood'), { clipPath: 'inset(0 0 0 0%)' });
    gsap.set(this.$('.mn-watermark'), { opacity: 0.09, xPercent: 0 });
    gsap.set(this.$('.mn-logo'), { opacity: 1, clipPath: 'inset(0 0% 0 0)', x: 0 });
    gsap.set(this.$('.mn-rule'), { scaleX: 1 });
    gsap.set(this.$('.mn-role'), { opacity: 1, y: 0 });
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const tl = this.timeline();
    if (step > 0 || immediate) this.settleLead();

    const pills = this.$$('.mn-pill');
    if (forward && step === 1) {
      tl.fromTo(this.$('.mn-pills__line'), { scaleX: 0 }, { scaleX: 1, duration: 1, ease: 'expo.inOut' }, 0.1);
      tl.fromTo(pills, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.6, stagger: 0.18, ease: 'expo.out' }, 0.3);
    } else {
      gsap.set(this.$('.mn-pills__line'), { scaleX: 1 });
      gsap.set(pills, { opacity: 1, y: 0 });
    }

    const statement = this.$('.mn-statement');
    settleWords(statement);
    if (forward && step === 2) tl.add(revealWords(statement), 0.2);
  }
}
